/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  ColorLens as ColorLensIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/lab';
import {
  getAllTenants, createTenant, updateTenant, deleteTenant, getAllPlans
} from '../../utils/api';

function TenantManagement() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [formData, setFormData] = useState({
    companyName: '',
    subdomain: '',
    planId: '',
    contactEmail: '',
    contactName: '',
    contactPhone: '',
    status: 'active',
    bucketName: '',
    logoUrl: '',
    signatureImage: '',
    signatureName: 'La Direction',
    primaryColor: '#3f51b5',
    secondaryColor: '#f50057',
    maxUsers: 10,
    trialEndsAt: null,
    dbConnectionString: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tenantsData, plansData] = await Promise.all([
        getAllTenants(),
        getAllPlans()
      ]);
      setTenants(tenantsData);
      setPlans(plansData);
    }
    catch (err) {
      console.error('Erreur lors du chargement des tenants:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTabIndex(newValue);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      companyName: '',
      subdomain: '',
      planId: '',
      contactEmail: '',
      contactName: '',
      contactPhone: '',
      status: 'active',
      bucketName: '',
      logoUrl: '',
      signatureImage: '',
      signatureName: 'La Direction',
      primaryColor: '#3f51b5',
      secondaryColor: '#f50057',
      maxUsers: 10,
      trialEndsAt: null,
      dbConnectionString: '',
    });
    setCurrentTabIndex(0);
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (tenant) => {
    setCurrentTenant(tenant);
    setFormData({
      companyName: tenant.company_name,
      subdomain: tenant.subdomain,
      planId: tenant.plan_id || '',
      contactEmail: tenant.contact_email || '',
      contactName: tenant.contact_name || '',
      contactPhone: tenant.contact_phone || '',
      status: tenant.status,
      bucketName: tenant.bucket_name || '',
      logoUrl: tenant.logo_url || '',
      signatureImage: tenant.signature_image || '',
      signatureName: tenant.signature_name || 'La Direction',
      primaryColor: tenant.primary_color || '#3f51b5',
      secondaryColor: tenant.secondary_color || '#f50057',
      maxUsers: tenant.max_users || 10,
      trialEndsAt: tenant.trial_ends_at ? new Date(tenant.trial_ends_at) : null,
      dbConnectionString: tenant.db_connection_string || '',
    });
    setCurrentTabIndex(0);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (tenant) => {
    setCurrentTenant(tenant);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTrialDateChange = (newDate) => {
    setFormData({ ...formData, trialEndsAt: newDate });
  };

  const handleCreateTenant = async () => {
    try {
      const tenantData = {
        companyName: formData.companyName,
        subdomain: formData.subdomain,
        planId: formData.planId || null,
        contactEmail: formData.contactEmail,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        status: formData.status,
        bucketName: formData.bucketName,
        logoUrl: formData.logoUrl,
        signatureImage: formData.signatureImage,
        signatureName: formData.signatureName,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        maxUsers: parseInt(formData.maxUsers, 10),
        trialEndsAt: formData.trialEndsAt,
        dbConnectionString: formData.dbConnectionString,
      };
      await createTenant(tenantData);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Tenant créé avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la création du tenant:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la création du tenant',
        severity: 'error',
      });
    }
  };

  const handleUpdateTenant = async () => {
    try {
      const updateData = {
        companyName: formData.companyName,
        planId: formData.planId || null,
        contactEmail: formData.contactEmail,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        status: formData.status,
        bucketName: formData.bucketName,
        logoUrl: formData.logoUrl,
        signatureImage: formData.signatureImage,
        signatureName: formData.signatureName,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        maxUsers: parseInt(formData.maxUsers, 10),
        trialEndsAt: formData.trialEndsAt,
        dbConnectionString: formData.dbConnectionString,
      };
      await updateTenant(currentTenant.tenant_id, updateData);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Tenant mis à jour avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la mise à jour du tenant:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la mise à jour du tenant',
        severity: 'error',
      });
    }
  };

  const handleDeleteTenant = async () => {
    try {
      await deleteTenant(currentTenant.tenant_id);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Tenant supprimé avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la suppression du tenant:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la suppression du tenant',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleManageFeatures = (tenantId) => {
    navigate(`/admin/tenants/${tenantId}/features`);
  };

  const renderTabContent = () => {
    switch (currentTabIndex) {
      case 0: // Informations générales
        return (
          <>
            <TextField
              autoFocus
              margin="dense"
              name="companyName"
              label="Nom de l'entreprise"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.companyName}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="subdomain"
              label="Sous-domaine"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.subdomain}
              onChange={handleInputChange}
              required
              disabled={openEditDialog}
              helperText={openEditDialog ? 'Le sous-domaine ne peut pas être modifié' : 'Sera utilisé comme {subdomain}.drivesoft.fr'}
              sx={{ mb: 2 }}
            />
            <TextField
              select
              margin="dense"
              name="planId"
              label="Plan"
              fullWidth
              variant="outlined"
              value={formData.planId}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value="">
                <em>Aucun plan</em>
              </MenuItem>
              {plans.map((plan) => (
                <MenuItem key={plan.plan_id} value={plan.plan_id}>
                  {plan.name} ({plan.price}€ / {plan.billing_cycle})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="dbConnectionString"
              label="Chaîne de connexion BDD"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.dbConnectionString}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="bucketName"
              label="Nom du bucket de stockage"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.bucketName}
              onChange={handleInputChange}
              required
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                margin="dense"
                name="maxUsers"
                label="Nombre max d'utilisateurs"
                type="number"
                variant="outlined"
                value={formData.maxUsers}
                onChange={handleInputChange}
                sx={{ flex: 1 }}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Fin de la période d'essai"
                  value={formData.trialEndsAt}
                  onChange={handleTrialDateChange}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" fullWidth sx={{ flex: 1 }} />
                  )}
                />
              </LocalizationProvider>
            </Box>
            <TextField
              select
              margin="dense"
              name="status"
              label="Statut"
              fullWidth
              variant="outlined"
              value={formData.status}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            >
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
              <MenuItem value="suspended">Suspendu</MenuItem>
              <MenuItem value="trial">Période d'essai</MenuItem>
            </TextField>
          </>
        );
      case 1: // Contact
        return (
          <>
            <TextField
              margin="dense"
              name="contactEmail"
              label="Email de contact"
              type="email"
              fullWidth
              variant="outlined"
              value={formData.contactEmail}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="contactName"
              label="Nom de contact"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.contactName}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="contactPhone"
              label="Téléphone de contact"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.contactPhone}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
          </>
        );
      case 2: // Personnalisation
        return (
          <>
            <TextField
              margin="dense"
              name="logoUrl"
              label="URL du logo"
              type="url"
              fullWidth
              variant="outlined"
              value={formData.logoUrl}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ImageIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            {formData.logoUrl && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={formData.logoUrl}
                  alt="Logo de l'entreprise"
                  style={{ maxHeight: '100px', maxWidth: '100%' }}
                />
              </Box>
            )}
            <TextField
              margin="dense"
              name="signatureImage"
              label="URL de l'image de signature"
              type="url"
              fullWidth
              variant="outlined"
              value={formData.signatureImage}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="signatureName"
              label="Nom de signature"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.signatureName}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                margin="dense"
                name="primaryColor"
                label="Couleur primaire"
                type="color"
                fullWidth
                variant="outlined"
                value={formData.primaryColor}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLensIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                margin="dense"
                name="secondaryColor"
                label="Couleur secondaire"
                type="color"
                fullWidth
                variant="outlined"
                value={formData.secondaryColor}
                onChange={handleInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLensIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
              />
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3
      }}
      >
        <Typography variant="h4">
          Gestion des Tenants
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Ajouter un Tenant
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="table des tenants">
            <TableHead>
              <TableRow>
                <TableCell>Nom de l'entreprise</TableCell>
                <TableCell>Sous-domaine</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Utilisateurs</TableCell>
                <TableCell>Période d'essai</TableCell>
                <TableCell>Date de création</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants
                .slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
                .map((tenant) => {
                  // Trouver le nom du plan
                  const plan = plans.find((p) => p.plan_id === tenant.plan_id);
                  const planName = plan ? plan.name : 'Aucun plan';

                  return (
                    <TableRow
                      key={tenant.tenant_id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {tenant.company_name}
                        {tenant.logo_url && (
                          <Box component="span" sx={{ ml: 1, verticalAlign: 'middle' }}>
                            <img
                              src={tenant.logo_url}
                              alt={`Logo ${tenant.company_name}`}
                              style={{ height: '20px', width: 'auto' }}
                            />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{tenant.subdomain}</TableCell>
                      <TableCell>{planName}</TableCell>
                      <TableCell>
                        <Chip
                          label={tenant.status}
                          color={
                            tenant.status === 'active' ? 'success'
                              : tenant.status === 'trial' ? 'info'
                                : tenant.status === 'suspended' ? 'warning' : 'error'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {tenant.contact_email && (
                          <Typography variant="body2">{tenant.contact_email}</Typography>
                        )}
                        {tenant.contact_name && (
                          <Typography variant="body2" color="text.secondary">
                            {tenant.contact_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {tenant.max_users || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {tenant.trial_ends_at
                          ? new Date(tenant.trial_ends_at).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {new Date(tenant.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleManageFeatures(tenant.tenant_id)}
                          title="Gérer les fonctionnalités"
                        >
                          <SettingsIcon />
                        </IconButton>
                        <IconButton
                          color="info"
                          onClick={() => handleOpenEditDialog(tenant)}
                          title="Modifier"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleOpenDeleteDialog(tenant)}
                          title="Supprimer"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Aucun tenant trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={tenants.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* Create/Edit Dialog with Tabs */}
      <Dialog
        open={openCreateDialog || openEditDialog}
        onClose={handleCloseDialogs}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {openCreateDialog ? 'Ajouter un nouveau tenant' : 'Modifier le tenant'}
        </DialogTitle>
        <Tabs
          value={currentTabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="tenant form tabs"
        >
          <Tab label="Informations générales" />
          <Tab label="Contact" />
          <Tab label="Personnalisation" />
        </Tabs>
        <DialogContent>
          {renderTabContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button
            onClick={openCreateDialog ? handleCreateTenant : handleUpdateTenant}
            variant="contained"
          >
            {openCreateDialog ? 'Créer' : 'Mettre à jour'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Supprimer le tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer le tenant <strong>{currentTenant?.company_name}</strong> ?
            Cette action est irréversible et supprimera toutes les données associées.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleDeleteTenant} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TenantManagement;
